// Owner Home scope + landing rule (docs/design/console-personas.md §3.2).
// Presentation-only — server still enforces company access.

import type { CompanySlug, DashboardGroup } from "./types.js";

export type HomeScope =
  | { kind: "all" }
  | { kind: "group"; groupId: string }
  | { kind: "company"; slug: CompanySlug };

export function scopeKey(scope: HomeScope): string {
  if (scope.kind === "all") return "all";
  if (scope.kind === "group") return `group:${scope.groupId}`;
  // The slug here is an internal key, never words. A branded value may not be
  // interpolated, concatenated or cast, so `valueOf()` is the explicit
  // primitive conversion at this wire boundary.
  return `company:${scope.slug.valueOf()}`;
}

/** Pre-groups default = All companies; else the exactly-one default group. */
export function defaultHomeScope(groups: readonly DashboardGroup[]): HomeScope {
  const def = groups.find((g) => g.isDefault);
  if (def) return { kind: "group", groupId: def.id };
  return { kind: "all" };
}

/**
 * A cold open lands on the owner's own company, not the fleet view: the last
 * company he used when it still exists, else the first member of the default
 * group, else the first active company. The all-companies view stays an
 * explicit choice in the switcher.
 */
export function initialHomeScope(
  companies: readonly { slug: CompanySlug }[],
  groups: readonly DashboardGroup[],
  lastCompany: string | null,
): { scope: HomeScope; company: CompanySlug } {
  const slugs = companies.map((company) => company.slug);
  if (slugs.length === 0) return { scope: { kind: "all" }, company: "" };

  if (lastCompany !== null && slugs.includes(lastCompany)) {
    return { scope: { kind: "company", slug: lastCompany }, company: lastCompany };
  }

  const def = groups.find((group) => group.isDefault);
  const firstOfGroup = def?.memberSlugs.find((slug) => slugs.includes(slug));
  const first = firstOfGroup ?? slugs[0] ?? "";
  return { scope: { kind: "company", slug: first }, company: first };
}

/**
 * "Groups" (All companies and saved groups) is extra chrome for an owner with
 * a saved group or enough companies that a flat company list is unwieldy.
 */
export function switcherShowsGroups(
  groups: readonly DashboardGroup[],
  companyCount: number,
): boolean {
  return groups.length > 0 || companyCount > 3;
}

export function resolveScopeMembers(
  scope: HomeScope,
  groups: readonly DashboardGroup[],
  allCompanySlugs: readonly string[],
): string[] {
  if (scope.kind === "all") return [...allCompanySlugs];
  if (scope.kind === "company") return [scope.slug];
  const group = groups.find((g) => g.id === scope.groupId);
  if (!group) return [...allCompanySlugs];
  // Preserve stored member order; drop unknown/inactive slugs quietly.
  const known = new Set(allCompanySlugs);
  return group.memberSlugs.filter((slug) => known.has(slug));
}

/**
 * Landing rule: last-visited-in-group this session, else first member
 * (stored member order). From All companies, same with the full list.
 */
export function landCompanyInScope(
  scope: HomeScope,
  groups: readonly DashboardGroup[],
  allCompanySlugs: readonly string[],
  lastVisitedByScope: ReadonlyMap<string, string>,
): string | null {
  const members = resolveScopeMembers(scope, groups, allCompanySlugs);
  if (members.length === 0) return null;
  const last = lastVisitedByScope.get(scopeKey(scope));
  if (last && members.includes(last)) return last;
  return members[0] ?? null;
}

export function scopeLabel(
  scope: HomeScope,
  groups: readonly DashboardGroup[],
): string {
  if (scope.kind === "all") return "All companies";
  if (scope.kind === "company") return "This company";
  const group = groups.find((g) => g.id === scope.groupId);
  return group?.name ?? "Group";
}

/**
 * Switcher entry order (§3.2): default group · All companies · other saved
 * groups · divider · single companies. Caller appends New group… separately.
 */
export function orderedSwitcherGroups(
  groups: readonly DashboardGroup[],
): { defaultGroup: DashboardGroup | null; others: DashboardGroup[] } {
  const defaultGroup = groups.find((g) => g.isDefault) ?? null;
  const others = groups
    .filter((g) => !g.isDefault)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  return { defaultGroup, others };
}

export function encodeScopeValue(scope: HomeScope): string {
  if (scope.kind === "all") return "all";
  if (scope.kind === "group") return `group:${scope.groupId}`;
  // The slug here is an internal key, never words. A branded value may not be
  // interpolated, concatenated or cast, so `valueOf()` is the explicit
  // primitive conversion at this wire boundary.
  return `company:${scope.slug.valueOf()}`;
}

export function parseScopeValue(value: string): HomeScope | "new-group" | null {
  if (value === "all") return { kind: "all" };
  if (value === "new-group") return "new-group";
  if (value.startsWith("group:")) {
    const groupId = value.slice("group:".length);
    if (groupId.length === 0) return null;
    return { kind: "group", groupId };
  }
  if (value.startsWith("company:")) {
    const slug = value.slice("company:".length);
    if (slug.length === 0) return null;
    return { kind: "company", slug };
  }
  return null;
}
